import React, { useState } from 'react';
import { checkItem, saveItem } from '../api/gameBuilderAPI';
import { CARRY, HOLDS, ItemPicker, Labelled } from './ItemFields';

/**
 * Edit one inventory item in the item schema (backend schemas/item.py, inventory/README.md) - only items that keep to
 * it (the catalog disables Edit for the rest: a drawn view, a document, spots on a picture...). What it is is said as in a
 * mission (ItemFields): can the player pick it up, can things go in or on it. Then its states, what the player can do,
 * its data, what it's used with, what missions may ask of it, what the AI is told, and the sample mission that proves it
 * works. Check asks the server (nothing saved); Save stores it only when it passes - a new version when it plays
 * differently, and the missions built with it rebuild.
 */
const copy = (x) => JSON.parse(JSON.stringify(x));
const words = (text) => text.split(',').map((w) => w.trim()).filter(Boolean);
const carryOf = (f) => (!f.isCollectible ? 'no' : f.isCarried === false ? 'push' : 'carry');
const holdsOf = (f) => f.allows?.holds?.relation || (f.container ? 'in' : '');
const freeId = (base, taken) => {
  let id = base;
  for (let k = 2; taken.includes(id); k += 1) id = `${base}_${k}`;
  return id;
};
const NEW_CONTAINER = { accepts: ['thing', 'tool', 'part', 'document', 'dish'], slots: [], takeOut: true };

// a state renamed everywhere it's named
const renameState = (f, from, to) => {
  const swap = (s) => (s === from ? to : s);
  f.states.forEach((s) => { s.id = swap(s.id); });
  f.options.forEach((o) => { o.from = o.from.map(swap); if (o.to) o.to = swap(o.to); });
  (f.combinations || []).forEach((c) => { c.from = (c.from || []).map(swap); c.to = swap(c.to); });
  if (f.container?.openIn) f.container.openIn = f.container.openIn.map(swap);
  if (f.allows?.status) { f.allows.status.on = swap(f.allows.status.on); f.allows.status.off = swap(f.allows.status.off); }
  if (f.allows?.rings) f.allows.rings = swap(f.allows.rings);
  if (f.look.states[from]) { f.look.states[to] = f.look.states[from]; delete f.look.states[from]; }
};
// a data field renamed everywhere it's named
const renameField = (f, from, to) => {
  f.data = Object.fromEntries(Object.entries(f.data).map(([k, v]) => [k === from ? to : k, v]));
  f.options.forEach((o) => { if (o.answer === from) o.answer = to; });
  ['has', 'talks'].forEach((k) => { if (f.allows?.[k] === from) f.allows[k] = to; });
  if (f.look.label === from) f.look.label = to;
};

function Chips({ label, all, on, onToggle }) {
  return (
    <div className="gb-ie-chips" role="group" aria-label={label}>
      {all.map((v) => (
        <button key={v} type="button" className={`gb-path-move ${on.includes(v) ? 'is-on' : ''}`} aria-pressed={on.includes(v)}
          onClick={() => onToggle(v)}>{v}</button>
      ))}
    </div>
  );
}

function JsonField({ label, value, onChange }) {
  const [text, setText] = useState(JSON.stringify(value ?? {}, null, 1));
  const [bad, setBad] = useState('');
  return (
    <Labelled label={label} hint={bad || 'JSON'} wide>
      <textarea aria-label={label} value={text} rows={3} onChange={(e) => {
        setText(e.target.value);
        try { onChange(JSON.parse(e.target.value || '{}')); setBad(''); } catch { setBad('Not valid JSON yet - not used until it is'); }
      }} />
    </Labelled>
  );
}

export default function ItemEditor({ item, choices, itemIds, onSaved, onCancel }) {
  const [form, setForm] = useState(() => copy(item.form));
  const [checked, setChecked] = useState(null);      // null | [] (passes) | [problems]
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const edit = (fn) => { setForm((f) => { const n = copy(f); fn(n); return n; }); setChecked(null); };

  const stateIds = form.states.map((s) => s.id);
  const fields = Object.keys(form.data);
  const familyOf = (mechanic) => choices.family?.[mechanic] || 'confirm';
  const mechanics = Object.keys(choices.mechanics || {});
  const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const fieldSelect = (label, value, onChange, none = '(none)') => (
    <select aria-label={label} value={value || ''} onChange={(e) => onChange(e.target.value || undefined)}>
      <option value="">{none}</option>
      {fields.map((k) => <option key={k} value={k}>{k}</option>)}
    </select>
  );

  const run = async (fn) => {
    setBusy(true); setError('');
    try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const check = () => run(async () => setChecked((await checkItem(item.id, form)).problems));
  const save = () => run(async () => onSaved(await saveItem(item.id, form)));

  const holds = holdsOf(form);
  const relations = choices.relations || ['in', 'on'];
  return (
    <div className="gb-item-editor" aria-label={`Edit ${item.name}`}>
      <header className="gb-catalog-head">
        <div>
          <h2>Edit {item.name} <span className="gb-muted gb-small">v{form.version}</span></h2>
          <p className="gb-muted gb-small">Saved only when it passes every check and its sample mission can be won. A change to how it
            plays makes a new version, and the simulations built with it are rebuilt with the change.</p>
        </div>
        <button type="button" className="gb-btn gb-btn-quiet" onClick={onCancel}>Back to the inventory</button>
      </header>

      <section aria-label="What it is">
        <h3>What it is</h3>
        <div className="gb-ie-row">
          <Labelled label="Name"><input type="text" aria-label="Name" value={form.name} onChange={(e) => edit((f) => { f.name = e.target.value; })} /></Labelled>
          <Labelled label="Icon"><input type="text" aria-label="Icon" value={form.look.icon} size={3} onChange={(e) => edit((f) => { f.look.icon = e.target.value; })} /></Labelled>
          <Labelled label="Kind">
            <select aria-label="Kind" value={form.kind} onChange={(e) => edit((f) => { f.kind = e.target.value; })}>
              {(choices.kinds || []).map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </Labelled>
          <Labelled label="Size">
            <select aria-label="Size" value={form.size || ''} onChange={(e) => edit((f) => { if (e.target.value) f.size = e.target.value; else delete f.size; })}>
              <option value="">worked out</option>
              {(choices.sizes || []).map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </Labelled>
          <Labelled label="Stands on the">
            <select aria-label="Placement" value={form.look.placement} onChange={(e) => edit((f) => { f.look.placement = e.target.value; })}>
              {(choices.placements || []).map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </Labelled>
        </div>
        <div className="gb-ie-row">
          <Labelled label="The player">
            <select aria-label="How it moves" value={carryOf(form)} onChange={(e) => edit((f) => {
              f.isCollectible = e.target.value !== 'no';
              if (e.target.value === 'push') f.isCarried = false; else delete f.isCarried;
            })}>
              {CARRY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Labelled>
          <Labelled label="Holds">
            <select aria-label="What it holds" value={holds} onChange={(e) => edit((f) => {
              const rel = e.target.value;
              if (!rel) { delete f.container; if (f.allows) delete f.allows.holds; return; }
              f.container = f.container || copy(NEW_CONTAINER);
              f.allows = { ...(f.allows || {}), holds: { ...(f.allows?.holds || {}), relation: rel } };
            })}>
              {HOLDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              {relations.filter((r) => !['in', 'on'].includes(r)).map((r) => <option key={r} value={r}>Things go {r} it</option>)}
            </select>
          </Labelled>
          <label className="gb-ie-check"><input type="checkbox" checked={form.isDisplayOnly}
            onChange={(e) => edit((f) => { f.isDisplayOnly = e.target.checked; })} /> Only to look at (nothing to do with it)</label>
          {item.review && (
            <label className="gb-ie-check"><input type="checkbox" checked={!form.review}
              onChange={(e) => edit((f) => { if (e.target.checked) delete f.review; else f.review = true; })} /> Checked - no longer &ldquo;To review&rdquo;</label>
          )}
        </div>
        {form.container && (
          <div className="gb-ie-row">
            <Labelled label="Its places inside (optional)" hint="Comma-separated - e.g. Top shelf, Bottom shelf">
              <input type="text" aria-label="Places inside" value={Array.isArray(form.container.slots) ? form.container.slots.join(', ') : form.container.slots}
                onChange={(e) => edit((f) => { f.container.slots = words(e.target.value); })} />
            </Labelled>
            <label className="gb-ie-check"><input type="checkbox" checked={form.container.takeOut}
              onChange={(e) => edit((f) => { f.container.takeOut = e.target.checked; })} /> Things can be taken out again</label>
            {stateIds.length > 1 && (
              <Labelled label="Filled only when">
                <Chips label="States it can be filled in" all={stateIds} on={form.container.openIn || []}
                  onToggle={(s) => edit((f) => { const v = toggle(f.container.openIn || [], s); if (v.length) f.container.openIn = v; else delete f.container.openIn; })} />
              </Labelled>
            )}
          </div>
        )}
      </section>

      <section aria-label="States">
        <h3>Its states</h3>
        <p className="gb-small gb-muted">What it can be. One is how it starts; a mission may start it in another marked so.</p>
        <ul className="gb-ie-list">
          {form.states.map((s, i) => (
            <li key={i} className="gb-ie-row">
              <Labelled label="State"><input type="text" aria-label={`State ${i + 1}`} value={s.id}
                onChange={(e) => edit((f) => renameState(f, s.id, e.target.value))} /></Labelled>
              <label className="gb-ie-check"><input type="radio" name="gb-ie-start" checked={!!s.start}
                onChange={() => edit((f) => f.states.forEach((x, k) => { if (k === i) x.start = true; else delete x.start; }))} /> Starts here</label>
              <label className="gb-ie-check"><input type="checkbox" checked={!!s.canStart}
                onChange={(e) => edit((f) => { if (e.target.checked) f.states[i].canStart = true; else delete f.states[i].canStart; })} /> Can start here</label>
              <Labelled label="What it tells the player"><input type="text" aria-label={`What ${s.id} says`} value={form.look.states[s.id]?.says || ''}
                onChange={(e) => edit((f) => { f.look.states[s.id] = { ...(f.look.states[s.id] || {}), says: e.target.value || undefined }; })} /></Labelled>
              <Labelled label="Shows">
                <select aria-label={`How ${s.id} shows`} value={form.look.states[s.id]?.effect || ''}
                  onChange={(e) => edit((f) => { f.look.states[s.id] = { ...(f.look.states[s.id] || {}), effect: e.target.value || undefined }; })}>
                  <option value="">as it is</option>
                  {(choices.effects || []).map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </Labelled>
              <button type="button" className="gb-link" aria-label={`Remove state ${s.id}`} disabled={form.states.length < 2}
                onClick={() => edit((f) => { f.states.splice(i, 1); delete f.look.states[s.id]; })}>✕</button>
            </li>
          ))}
        </ul>
        <button type="button" className="gb-link" onClick={() => edit((f) => {
          f.states.push({ id: freeId('new_state', f.states.map((x) => x.id)) });
        })}>+ Add a state</button>
      </section>

      <section aria-label="What the player can do">
        <h3>What the player can do</h3>
        <ul className="gb-ie-list">
          {form.options.map((o, i) => {
            const family = familyOf(o.mechanic);
            const styles = choices.styles?.[o.mechanic] || [];
            const set = (fn) => edit((f) => fn(f.options[i]));
            return (
              <li key={i}>
                <div className="gb-ie-row">
                  <Labelled label="Button says"><input type="text" aria-label={`Action ${i + 1} label`} value={o.label} onChange={(e) => set((x) => { x.label = e.target.value; })} /></Labelled>
                  <Labelled label="Id"><input type="text" aria-label={`Action ${i + 1} id`} value={o.id} size={12} onChange={(e) => set((x) => { x.id = e.target.value; })} /></Labelled>
                  <Labelled label="How it's played" hint={choices.mechanics?.[o.mechanic]}>
                    <select aria-label={`Action ${i + 1} mechanic`} value={o.mechanic} onChange={(e) => set((x) => { x.mechanic = e.target.value; if (!(choices.styles?.[x.mechanic] || []).includes(x.style)) delete x.style; })}>
                      {!mechanics.includes(o.mechanic) && <option value={o.mechanic}>{o.mechanic}</option>}
                      {mechanics.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </Labelled>
                  <button type="button" className="gb-link" aria-label={`Remove action ${o.label || o.id}`}
                    onClick={() => edit((f) => { f.options.splice(i, 1); f.sample.steps = (f.sample.steps || []).filter((x) => x !== o.id); })}>✕</button>
                </div>
                <div className="gb-ie-row">
                  <Labelled label="Works when it is"><Chips label={`States action ${i + 1} works in`} all={stateIds} on={o.from} onToggle={(s) => set((x) => { x.from = toggle(x.from, s); })} /></Labelled>
                  <Labelled label="Then it is">
                    <select aria-label={`Action ${i + 1} moves it to`} value={o.to || ''} onChange={(e) => set((x) => { if (e.target.value) x.to = e.target.value; else delete x.to; })}>
                      <option value="">(stays as it is)</option>
                      {stateIds.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Labelled>
                  {family !== 'confirm' && (
                    <Labelled label="Right answer from">{fieldSelect(`Action ${i + 1} answer`, o.answer, (v) => set((x) => { if (v) x.answer = v; else delete x.answer; }))}</Labelled>
                  )}
                  {family !== 'confirm' && (
                    <Labelled label="Records as"><input type="text" aria-label={`Action ${i + 1} records`} value={o.records || ''} size={10}
                      onChange={(e) => set((x) => { if (e.target.value) x.records = e.target.value; else delete x.records; })} /></Labelled>
                  )}
                  {styles.length > 0 && (
                    <Labelled label="Looks like">
                      <select aria-label={`Action ${i + 1} style`} value={o.style || ''} onChange={(e) => set((x) => { if (e.target.value) x.style = e.target.value; else delete x.style; })}>
                        <option value="">(default)</option>
                        {styles.map((k) => <option key={k} value={k}>{k}</option>)}
                      </select>
                    </Labelled>
                  )}
                  {o.mechanic === 'hold' && (
                    <Labelled label="Hold for (seconds)"><input type="number" aria-label={`Action ${i + 1} seconds`} min={1} value={o.seconds || ''}
                      onChange={(e) => set((x) => { if (e.target.value) x.seconds = Number(e.target.value); else delete x.seconds; })} /></Labelled>
                  )}
                  {[['undo', 'Puts it back (never a step)'], ['rightOnly', 'Only the right answer moves it'], ['needsWatched', 'Waits for what it watches']]
                    .filter(([k]) => k !== 'needsWatched' || o.needsWatched).map(([k, l]) => (
                      <label key={k} className="gb-ie-check"><input type="checkbox" checked={!!o[k]}
                        onChange={(e) => set((x) => { if (e.target.checked) x[k] = true; else delete x[k]; })} /> {l}</label>
                    ))}
                </div>
              </li>
            );
          })}
        </ul>
        <button type="button" className="gb-link" onClick={() => edit((f) => {
          f.options.push({ id: freeId('new_action', f.options.map((x) => x.id)), label: 'Do it', mechanic: 'confirm', from: [f.states[0].id] });
        })}>+ Add an action</button>
      </section>

      <section aria-label="Its data">
        <h3>Its data</h3>
        <p className="gb-small gb-muted">Filled in by each mission - the code to enter, the right option and look-alikes, an amount.</p>
        <ul className="gb-ie-list">
          {Object.entries(form.data).map(([key, d], i) => (
            <li key={i} className="gb-ie-row">
              <Labelled label="Field"><input type="text" aria-label={`Field ${i + 1}`} value={key} size={12} onChange={(e) => edit((f) => renameField(f, key, e.target.value))} /></Labelled>
              <Labelled label="Type">
                <select aria-label={`Field ${key} type`} value={d.type} onChange={(e) => edit((f) => { f.data[key].type = e.target.value; })}>
                  {!(choices.data_types || []).includes(d.type) && <option value={d.type}>{d.type}</option>}
                  {(choices.data_types || []).map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </Labelled>
              <Labelled label="What it's for (told to the AI)"><input type="text" aria-label={`Field ${key} about`} value={d.about || ''}
                onChange={(e) => edit((f) => { f.data[key].about = e.target.value; })} /></Labelled>
              <Labelled label="When left out, says"><input type="text" aria-label={`Field ${key} default`} value={d.default || ''} size={10}
                onChange={(e) => edit((f) => { if (e.target.value) f.data[key].default = e.target.value; else delete f.data[key].default; })} /></Labelled>
              {[['optional', 'Optional'], ['workedOut', 'Worked out by the player']].map(([k, l]) => (
                <label key={k} className="gb-ie-check"><input type="checkbox" checked={!!d[k]}
                  onChange={(e) => edit((f) => { if (e.target.checked) f.data[key][k] = true; else delete f.data[key][k]; })} /> {l}</label>
              ))}
              <button type="button" className="gb-link" aria-label={`Remove field ${key}`} onClick={() => edit((f) => { delete f.data[key]; })}>✕</button>
            </li>
          ))}
        </ul>
        <button type="button" className="gb-link" onClick={() => edit((f) => {
          f.data[freeId('new_field', Object.keys(f.data))] = { type: 'text', about: '' };
        })}>+ Add a field</button>
      </section>

      <section aria-label="Used with">
        <h3>Used with another item</h3>
        <p className="gb-small gb-muted">Another item the player uses on this one, and the state that puts it in (granules on a spill: cleaned up).</p>
        <ul className="gb-ie-list">
          {(form.combinations || []).map((c, i) => (
            <li key={i} className="gb-ie-row">
              <Labelled label="Item used on it">
                <ItemPicker label={`Combination ${i + 1} item`} value={c.with} options={itemIds.map((id) => ({ value: id, label: id }))}
                  onChange={(v) => edit((f) => { f.combinations[i].with = v; })} />
              </Labelled>
              <Labelled label="When it is"><Chips label={`States combination ${i + 1} works in`} all={stateIds} on={c.from || []}
                onToggle={(s) => edit((f) => { f.combinations[i].from = toggle(f.combinations[i].from || [], s); })} /></Labelled>
              <Labelled label="Then it is">
                <select aria-label={`Combination ${i + 1} to`} value={c.to} onChange={(e) => edit((f) => { f.combinations[i].to = e.target.value; })}>
                  {stateIds.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Labelled>
              <button type="button" className="gb-link" aria-label={`Remove combination ${i + 1}`}
                onClick={() => edit((f) => { f.combinations.splice(i, 1); })}>✕</button>
            </li>
          ))}
        </ul>
        <button type="button" className="gb-link" onClick={() => edit((f) => {
          f.combinations = [...(f.combinations || []), { with: itemIds[0], from: [], to: f.states[0].id }];
        })}>+ Add an item used on it</button>
      </section>

      <section aria-label="What missions can ask for">
        <h3>What missions can ask for</h3>
        <label className="gb-ie-check"><input type="checkbox" checked={!!form.allows}
          onChange={(e) => edit((f) => { if (e.target.checked) f.allows = {}; else delete f.allows; })} /> Offered to missions written as how things must end</label>
        {form.allows && (
          <>
            <div className="gb-ie-row">
              <label className="gb-ie-check"><input type="checkbox" checked={!!form.allows.status} disabled={stateIds.length < 2}
                onChange={(e) => edit((f) => {
                  if (e.target.checked) f.allows.status = { on: f.states[1].id, off: f.states[0].id, words: [f.states[1].id, f.states[0].id] };
                  else delete f.allows.status;
                })} /> A state a mission can ask for</label>
              {form.allows.status && ['on', 'off'].map((k, w) => (
                <React.Fragment key={k}>
                  <Labelled label={w === 0 ? 'One state' : 'The other'}>
                    <select aria-label={`Status ${k}`} value={form.allows.status[k]} onChange={(e) => edit((f) => { f.allows.status[k] = e.target.value; })}>
                      {stateIds.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Labelled>
                  <Labelled label="Creators read it as"><input type="text" aria-label={`Status ${k} word`} value={form.allows.status.words?.[w] || ''} size={12}
                    onChange={(e) => edit((f) => { const ws = [...(f.allows.status.words || ['', ''])]; ws[w] = e.target.value; f.allows.status.words = ws; })} /></Labelled>
                </React.Fragment>
              ))}
            </div>
            <div className="gb-ie-row">
              <Labelled label="Ends with a value from">{fieldSelect('Value field', form.allows.has, (v) => edit((f) => { if (v) f.allows.has = v; else delete f.allows.has; }))}</Labelled>
              <Labelled label="What they say, from">{fieldSelect('Talks field', form.allows.talks, (v) => edit((f) => { if (v) f.allows.talks = v; else delete f.allows.talks; }))}</Labelled>
              <Labelled label="Rings in">
                <select aria-label="Rings in" value={form.allows.rings || ''} onChange={(e) => edit((f) => { if (e.target.value) f.allows.rings = e.target.value; else delete f.allows.rings; })}>
                  <option value="">(never rings)</option>
                  {stateIds.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Labelled>
              <label className="gb-ie-check"><input type="checkbox" checked={!!form.allows.walksIn}
                onChange={(e) => edit((f) => { if (e.target.checked) f.allows.walksIn = true; else delete f.allows.walksIn; })} /> Can walk in</label>
              <Labelled label="What's in it is drawn as">
                <select aria-label="Holder" value={form.allows.holder || ''} onChange={(e) => edit((f) => { if (e.target.value) f.allows.holder = e.target.value; else delete f.allows.holder; })}>
                  <option value="">(default)</option>
                  {(choices.holders || []).map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </Labelled>
            </div>
          </>
        )}
      </section>

      <section aria-label="For the AI and the picture">
        <h3>For the AI and its picture</h3>
        <Labelled label="When to use it (told to the AI)" wide>
          <textarea aria-label="When to use it" value={form.ai.for} onChange={(e) => edit((f) => { f.ai.for = e.target.value; })} />
        </Labelled>
        <div className="gb-ie-row">
          <Labelled label="Settings it's offered in" hint="Comma-separated - e.g. kitchen, warehouse">
            <input type="text" aria-label="Tags" value={form.ai.tags.join(', ')} onChange={(e) => edit((f) => { f.ai.tags = words(e.target.value); })} />
          </Labelled>
          <Labelled label="Other names" hint="A mission object called this becomes this item">
            <input type="text" aria-label="Other names" value={(form.ai.aka || []).join(', ')}
              onChange={(e) => edit((f) => { const v = words(e.target.value); if (v.length) f.ai.aka = v; else delete f.ai.aka; })} />
          </Labelled>
        </div>
        <div className="gb-ie-row">
          <Labelled label="How it looks (for its picture)" wide>
            <input type="text" aria-label="Picture prompt" value={form.look.picturePrompt || ''}
              onChange={(e) => edit((f) => { f.look.picturePrompt = e.target.value || null; })} />
          </Labelled>
          <Labelled label="Words on it, from">{fieldSelect('Label field', form.look.label, (v) => edit((f) => { f.look.label = v || null; }))}</Labelled>
        </div>
      </section>

      <section aria-label="Sample mission">
        <h3>Sample mission</h3>
        <p className="gb-small gb-muted">A tiny mission that must build and be winnable - it's also what &ldquo;Try it&rdquo; plays.</p>
        <Labelled label="Steps, in order" hint="Tick the actions the sample does, in the order they're done.">
          <Chips label="Sample steps" all={form.options.map((o) => o.id)} on={form.sample.steps || []}
            onToggle={(id) => edit((f) => { f.sample.steps = toggle(f.sample.steps || [], id); })} />
        </Labelled>
        <p className="gb-small">{(form.sample.steps || []).join(' → ') || 'No steps'}</p>
        <div className="gb-ie-row">
          <JsonField label="Facts" value={form.sample.facts} onChange={(v) => edit((f) => { f.sample.facts = v; })} />
          <JsonField label="Data" value={form.sample.data} onChange={(v) => edit((f) => { f.sample.data = v; })} />
        </div>
      </section>

      <div className="gb-ie-bar">
        <button type="button" className="gb-btn gb-btn-quiet" disabled={busy} onClick={check}>Check</button>
        <button type="button" className="gb-btn" disabled={busy} onClick={save}>Save</button>
        <button type="button" className="gb-btn gb-btn-quiet" disabled={busy} onClick={onCancel}>Cancel</button>
        {busy && <span className="gb-spinner" />}
        {checked && checked.length === 0 && <span className="gb-ie-ok" role="status">✓ Passes every check - its sample can be won</span>}
      </div>
      {error && <div className="gb-error" role="alert"><span>{error}</span></div>}
      {checked && checked.length > 0 && (
        <div className="gb-error" role="alert"><ul className="gb-ie-problems">{checked.map((p) => <li key={p}>{p}</li>)}</ul></div>
      )}
    </div>
  );
}
